import { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Card,
  CardContent,
  Grid,
  CircularProgress,
  Alert,
  Chip,
} from "@mui/material";
import { useNavigate } from "react-router-dom";
import { collection, query, where, orderBy, getDocs } from "firebase/firestore";
import { firestore } from "@/firebase";
import { raveConverter } from "@/converters";
import { useAuthContext } from "@/context/AuthContext";
import { Rave } from "@t3/shared";

export const LiteRaveMyRaves = () => {
  const navigate = useNavigate();
  const { user } = useAuthContext();
  const [raves, setRaves] = useState<Rave[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!user) {
      navigate("/lite-raves/signup");
      return;
    }

    const fetchRaves = async () => {
      try {
        const ravesRef = collection(firestore, `company/${user.company}/raves`);
        const q = query(
          ravesRef.withConverter(raveConverter),
          where("sender.id", "==", user.id),
          where("type", "==", "lite"),
          orderBy("createdAt", "desc"),
        );

        const snapshot = await getDocs(q);
        const ravesData = snapshot.docs.map((doc) => doc.data());
        setRaves(ravesData);
      } catch (err) {
        console.error("Error fetching raves:", err);
        setError("Failed to load your raves");
      } finally {
        setLoading(false);
      }
    };

    fetchRaves();
  }, [user, navigate]);

  if (loading) {
    return (
      <Box
        sx={{
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          minHeight: "50vh",
        }}
      >
        <CircularProgress />
      </Box>
    );
  }

  return (
    <Box>
      <Typography variant="h3" sx={{ mb: 1 }}>
        My Lite Raves
      </Typography>
      <Typography variant="body1" color="text.secondary" sx={{ mb: 4 }}>
        All the raves you've created
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {raves.length === 0 && !error && (
        <Alert severity="info">
          You haven't created any lite raves yet. Create your first one to get
          started!
        </Alert>
      )}

      <Grid container spacing={3}>
        {raves.map((rave) => (
          <Grid size={{ xs: 12, sm: 6, md: 4 }} key={rave.id}>
            <Card
              sx={{
                cursor: "pointer",
                transition: "transform 0.2s, box-shadow 0.2s",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: 4,
                },
              }}
              onClick={() => navigate(`/lite-raves/${rave.id}`)}
            >
              <CardContent>
                <Box
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    mb: 2,
                  }}
                >
                  <Typography variant="h6" noWrap sx={{ flex: 1 }}>
                    {rave.title || "Untitled"}
                  </Typography>
                  <Chip
                    label={rave.status}
                    size="small"
                    color={rave.status === "activated" ? "success" : "default"}
                  />
                </Box>

                <Typography
                  variant="body2"
                  color="text.secondary"
                  sx={{
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    display: "-webkit-box",
                    WebkitLineClamp: 3,
                    WebkitBoxOrient: "vertical",
                    mb: 2,
                  }}
                >
                  {rave.description || "No description"}
                </Typography>

                <Typography variant="caption" color="text.secondary">
                  Created: {rave.createdAt.toFormat("MMM dd, yyyy")}
                </Typography>

                {rave.recipientIds.length > 0 && (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="caption" color="text.secondary">
                      Recipients: {rave.recipientIds.length}
                    </Typography>
                  </Box>
                )}
              </CardContent>
            </Card>
          </Grid>
        ))}
      </Grid>
    </Box>
  );
};
